import { AddNewIntegrationModal } from './AddNewIntegrationModal'
import { CallRailFormModal } from './configuration-forms/CallRailFormModal'
import { RingCentralFormModal } from './configuration-forms/RingCentralFormModal'
import { TwilioNumbersModal } from './configuration-forms/TwilioNumbersModal'
import { CallRailForm } from './setup-forms/CallRailForm'
import { RingCentralForm } from './setup-forms/RingCentralForm'
import { TwilioForm } from './setup-forms/TwilioForm'
import { AscendWorkspaceModalForm } from './setup-forms/AscendWorkspaceModalForm'
import { CarestackWorkspaceModalForm } from './setup-forms/CarestackWorkspaceModalForm'
import { FacebookWorkspaceModalForm } from './setup-forms/FacebookWorkspaceModalForm'
import { setWizardResume, type IntegrationName } from './workspaceRequirements'
import { closeModal, showModal } from 'app/lib/modal'
import { log } from 'lib/logging'
import type { ReactElement } from 'react'

const MODAL_OPTIONS = { isFullScreen: false, overflowHidden: false, removePadding: true }

function WizardStepBanner({ title, description }: { title: string; description: string }) {
  return (
    <div className="w-full max-w-[600px] px-4 pt-4">
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
        <p className="text-ui-sm font-medium text-amber-900">{title}</p>
        <p className="mt-1 text-body-sm text-amber-800">{description}</p>
      </div>
    </div>
  )
}

/**
 * Opens the workspace-level setup form as Step 1 of the location-tab "New
 * Integration" flow, for an integration whose workspace connection is missing.
 * Credential forms chain into the location form on success. Carestack/Ascend
 * workspace forms already include location mapping, so they complete on their
 * own. Facebook forms redirect to OAuth and resume via the sessionStorage flag.
 */
export function openWorkspaceSetupStep(integration: IntegrationName, onIntegrationAdded: () => void) {
  const backToPicker = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onIntegrationAdded} />,
      { title: `Add New Integration`, ...MODAL_OPTIONS, useRadix: true },
      false,
    )
  }

  const continueTo = (form: ReactElement, title: string) => {
    closeModal()
    showModal(form, { title: `${title} · Step 2 of 2`, ...MODAL_OPTIONS, useRadix: true }, false)
  }

  switch (integration) {
    case `CallRail`:
      showModal(
        <>
          <WizardStepBanner
            title="Step 1 of 2 · Connect CallRail to your workspace"
            description="Locations can't use CallRail until this connection exists. You'll link your location in the next step."
          />
          <CallRailForm
            onIntegrationChanged={onIntegrationAdded}
            onBack={backToPicker}
            onSuccess={() => continueTo(<CallRailFormModal onAdd={onIntegrationAdded} />, `CallRail`)}
          />
        </>,
        { title: `CallRail Workspace Integration`, ...MODAL_OPTIONS, useRadix: true },
        false,
      )
      break
    case `Twilio`:
      showModal(
        <>
          <WizardStepBanner
            title="Step 1 of 2 · Connect Twilio to your workspace"
            description="Locations can't use Twilio until this connection exists. You'll link your location in the next step."
          />
          <TwilioForm
            onIntegrationChanged={onIntegrationAdded}
            onBack={backToPicker}
            onSuccess={() => continueTo(<TwilioNumbersModal onAdd={onIntegrationAdded} />, `Twilio`)}
          />
        </>,
        { title: `Twilio Workspace Integration`, ...MODAL_OPTIONS, useRadix: true },
        false,
      )
      break
    case `RingCentral`:
      showModal(
        <>
          <WizardStepBanner
            title="Step 1 of 2 · Connect RingCentral to your workspace"
            description="Locations can't use RingCentral until this connection exists. You'll link your location in the next step."
          />
          <RingCentralForm
            onIntegrationChanged={onIntegrationAdded}
            onBack={backToPicker}
            onSuccess={() => continueTo(<RingCentralFormModal onAdd={onIntegrationAdded} />, `RingCentral`)}
          />
        </>,
        { title: `RingCentral Workspace Integration`, ...MODAL_OPTIONS, useRadix: true },
        false,
      )
      break
    case `Carestack`:
      showModal(
        <>
          <WizardStepBanner
            title="Workspace connection required · Carestack"
            description="Connect Carestack to your workspace first. This form then maps Carestack locations to your Trackable locations, completing setup."
          />
          <CarestackWorkspaceModalForm onIntegrationChanged={onIntegrationAdded} onBack={backToPicker} />
        </>,
        { title: `Carestack Workspace Integration`, ...MODAL_OPTIONS, useRadix: true },
        false,
      )
      break
    case `Ascend`:
      showModal(
        <>
          <WizardStepBanner
            title="Workspace connection required · Ascend"
            description="Connect Ascend to your workspace first. This form then maps Ascend locations to your Trackable locations, completing setup."
          />
          <AscendWorkspaceModalForm onIntegrationChanged={onIntegrationAdded} onBack={backToPicker} />
        </>,
        { title: `Ascend Workspace Integration`, ...MODAL_OPTIONS, useRadix: true },
        false,
      )
      break
    case `Facebook Ads`:
      showModal(
        <>
          <WizardStepBanner
            title="Step 1 of 2 · Connect Facebook Ads to your workspace"
            description="You'll be sent to Facebook to authorize the connection. When you return, we'll open the location setup automatically."
          />
          <FacebookWorkspaceModalForm
            onIntegrationChanged={onIntegrationAdded}
            integrationType="facebook-ads"
            onBeforeConnect={() => setWizardResume(`Facebook Ads`)}
            onBack={backToPicker}
          />
        </>,
        { title: `Facebook Ads Workspace Integration`, ...MODAL_OPTIONS, useRadix: true },
        false,
      )
      break
    case `Facebook Leads`:
      showModal(
        <>
          <WizardStepBanner
            title="Step 1 of 2 · Connect Facebook Leads to your workspace"
            description="You'll be sent to Facebook to authorize the connection. When you return, we'll open the location setup automatically."
          />
          <FacebookWorkspaceModalForm
            onIntegrationChanged={onIntegrationAdded}
            integrationType="facebook-leads"
            onBeforeConnect={() => setWizardResume(`Facebook Leads`)}
            onBack={backToPicker}
          />
        </>,
        { title: `Facebook Leads Workspace Integration`, ...MODAL_OPTIONS, useRadix: true },
        false,
      )
      break
    default:
      log(`No workspace setup step for integration`, integration)
      break
  }
}
